import { useMemo } from "react";
import {
  useGetSupplier2nd,
  useCreateSupplier2nd,
  useUpdateSupplier2nd,
  useGetReasonPriceChange,
  useCreateReasonPriceChange,
  useUpdateReasonPriceChange,
} from "@/hooks/masterHook";
import type { Master } from "@/api/masterService";

const systemMasterTabs = [
  { label: "SUPPLIER 2ND", value: 0 },
  { label: "REASON PRICE CHANGE", value: 1 },
];

export const useSystemMasterData = (tabValue: number) => {
  const {
    data: supplier2ndData,
    isLoading: isLoadingSupplier2nd,
    isError: isErrorSupplier2nd,
  } = useGetSupplier2nd({}, tabValue === 0);

  const {
    data: reasonPriceChangeData,
    isLoading: isLoadingReasonPriceChange,
    isError: isErrorReasonPriceChange,
  } = useGetReasonPriceChange({}, tabValue === 1);

  const isLoading = useMemo(() => {
    switch (tabValue) {
      case 0:
        return isLoadingSupplier2nd;
      case 1:
        return isLoadingReasonPriceChange;
      default:
        return false;
    }
  }, [isLoadingReasonPriceChange, isLoadingSupplier2nd, tabValue]);

  const isError = useMemo(() => {
    switch (tabValue) {
      case 0:
        return isErrorSupplier2nd;
      case 1:
        return isErrorReasonPriceChange;
      default:
        return false;
    }
  }, [isErrorReasonPriceChange, isErrorSupplier2nd, tabValue]);

  const rows = useMemo(() => {
    const dataMap: { [key: number]: Master[] } = {
      0: supplier2ndData || [],
      1: reasonPriceChangeData || [],
    };
    const sorted = [...(dataMap[tabValue] || [])].sort((a, b) => {
      if (a.is_active === b.is_active) {
        return a.id - b.id;
      }
      return a.is_active ? -1 : 1;
    });
    return sorted;
  }, [tabValue, supplier2ndData, reasonPriceChangeData]);

  const countActive = useMemo(
    () => rows.filter((row: Master) => row.is_active).length,
    [rows]
  );

  const countInactive = useMemo(
    () => rows.filter((row: Master) => !row.is_active).length,
    [rows]
  );

  const createSupplier2nd = useCreateSupplier2nd();
  const updateSupplier2nd = useUpdateSupplier2nd();
  const createReasonPriceChange = useCreateReasonPriceChange();
  const updateReasonPriceChange = useUpdateReasonPriceChange();

  const createMaster = useMemo(() => {
    switch (tabValue) {
      case 0:
        return createSupplier2nd;
      case 1:
        return createReasonPriceChange;
      default:
        return null;
    }
  }, [createReasonPriceChange, createSupplier2nd, tabValue]);

  const updateMaster = useMemo(() => {
    switch (tabValue) {
      case 0:
        return updateSupplier2nd;
      case 1:
        return updateReasonPriceChange;
      default:
        return null;
    }
  }, [updateReasonPriceChange, updateSupplier2nd, tabValue]);

  return {
    rows,
    countActive,
    countInactive,
    systemMasterTabs,
    isLoading,
    isError,
    createMaster,
    updateMaster,
  };
};
